// Pan/zoom controller that drives SVGs through their `viewBox`, never a CSS transform.
// WebKit rasterizes a CSS `transform: scale()` on inline SVG at base resolution (blurry);
// viewBox changes re-rasterize vectors at display resolution, so it stays crisp at any zoom.
// No ancestor transform also keeps `getScreenCTM()` reliable for coordinate consumers.
//
// The SVG fills `.selectable-region`; the controller sizes the region and keeps the
// viewBox at the region's aspect ratio so content always fills it.

export type ViewBox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type ViewBoxControllerOptions = {
  /** Minimum zoom, relative to fit. 1 = can't zoom out past the fit view. */
  minScale?: number;
  /** Maximum zoom, relative to fit. */
  maxScale?: number;
  /** Duration (ms) of animated zooms (double-click). */
  animationMs?: number;
};

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(Math.max(value, lo), hi);
}

/**
 * A region-aspect viewBox framing `bounds` expanded by `marginFactor`, centred on the bounds.
 * Held at `fitBox`'s aspect by growing the short side (never cropping content). Pure (no
 * clamping) so it's unit-testable — callers run it through `clampBox` before applying.
 */
export function frameBounds(bounds: ViewBox, fitBox: ViewBox, marginFactor: number): ViewBox {
  const cx = bounds.x + bounds.w / 2;
  const cy = bounds.y + bounds.h / 2;
  const aspect = fitBox.w / fitBox.h;
  const w = Math.max(bounds.w * marginFactor, bounds.h * marginFactor * aspect);
  const h = w / aspect;
  return {x: cx - w / 2, y: cy - h / 2, w, h};
}

export class ViewBoxController {
  private readonly svgs: SVGSVGElement[];
  private readonly viewport: HTMLElement;
  private readonly minScale: number;
  private readonly maxScale: number;
  private readonly animationMs: number;
  private readonly base: ViewBox; // the sheet's natural viewBox
  private fitBox: ViewBox; // region-aspect viewBox enclosing the whole sheet (scale 1)
  private cur: ViewBox;
  // Cached SVG screen box (viewBox-invariant); invalidated on resize/rebind.
  private cachedSvgRect: DOMRect | null = null;
  private rafId: number | null = null;
  private readonly listeners = new Set<() => void>(); // view-change subscribers

  constructor(svgs: SVGSVGElement[], viewport: HTMLElement, opts: ViewBoxControllerOptions = {}) {
    this.svgs = svgs;
    this.viewport = viewport;
    this.minScale = opts.minScale ?? 1;
    this.maxScale = opts.maxScale ?? 50;
    this.animationMs = opts.animationMs ?? 150;
    const vb = svgs[0].viewBox.baseVal;
    this.base = {x: vb.x, y: vb.y, w: vb.width, h: vb.height};
    // Expose the original viewBox so coordinate consumers can map screen coords to % of
    // the whole sheet, even though the live `viewBox` attribute changes as we zoom.
    const baseViewBox = `${vb.x} ${vb.y} ${vb.width} ${vb.height}`;
    for (const svg of svgs) svg.setAttribute('data-base-viewbox', baseViewBox);
    // Set by fit(); assigned here to satisfy definite-assignment.
    this.fitBox = {...this.base};
    this.cur = {...this.base};
    this.fit();
  }

  // ---- public API ----

  /** Zoom relative to the fit view: 1 = whole sheet, larger = zoomed in. */
  getScale(): number {
    return this.fitBox.w / this.cur.w;
  }

  /** Zoom to an absolute scale, keeping the point under (clientX, clientY) fixed. */
  zoomToPoint(scale: number, ev: {clientX: number; clientY: number}, opts?: {animate?: boolean}): void {
    this.cancelAnim(); // take over from any in-flight animation
    const s = clamp(scale, this.minScale, this.maxScale);
    const {fx, fy} = this.clientFraction(ev.clientX, ev.clientY);
    const ux = this.cur.x + fx * this.cur.w;
    const uy = this.cur.y + fy * this.cur.h;
    const w = this.fitBox.w / s;
    const h = this.fitBox.h / s;
    const target = this.clampBox({x: ux - fx * w, y: uy - fy * h, w, h});
    if (opts?.animate) {
      this.animateTo(target);
    } else {
      this.apply(target);
    }
  }

  /** Pan by a screen-pixel delta (drag). Positive dx = pointer moved right. */
  panByClient(dxClient: number, dyClient: number): void {
    const rect = this.svgRect();
    if (!rect.width || !rect.height) return;
    this.cancelAnim(); // a drag takes over from any in-flight zoom animation
    const dux = (dxClient / rect.width) * this.cur.w;
    const duy = (dyClient / rect.height) * this.cur.h;
    this.apply(this.clampBox({...this.cur, x: this.cur.x - dux, y: this.cur.y - duy}));
  }

  /** Zoom + pan so the given user-space bounds are framed (with margin) and centred. */
  fitBounds(bounds: ViewBox, marginFactor = 3): void {
    this.cancelAnim();
    this.apply(this.clampBox(frameBounds(bounds, this.fitBox, marginFactor)));
  }

  /**
   * Subscribe to view changes; the callback fires on every viewBox update. Returns an
   * unsubscribe fn. Used by overlays with no ancestor transform to reposition as the view moves.
   */
  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  /** Reset to the fit view. */
  reset(): void {
    this.cancelAnim();
    this.fit();
  }

  /** Recompute fit + region size for a new viewport, preserving zoom and centre. */
  onResize(): void {
    this.cancelAnim();
    this.cachedSvgRect = null; // the SVG's screen box may have changed
    const scale = this.getScale();
    const cx = this.cur.x + this.cur.w / 2;
    const cy = this.cur.y + this.cur.h / 2;
    this.sizeViewport();
    this.computeFit();
    const w = this.fitBox.w / scale;
    const h = this.fitBox.h / scale;
    this.apply(this.clampBox({x: cx - w / 2, y: cy - h / 2, w, h}));
  }

  /** Rebind to a swapped-in SVG: re-read its base viewBox and re-fit.  Existing listeners (which
   *  close over the controller) keep driving the new node. */
  rebind(svg: SVGSVGElement): void {
    this.cancelAnim();
    this.cachedSvgRect = null; // new node, new screen box
    this.svgs.splice(0, this.svgs.length, svg);
    const vb = svg.viewBox.baseVal;
    Object.assign(this.base, {x: vb.x, y: vb.y, w: vb.width, h: vb.height});
    svg.setAttribute('data-base-viewbox', `${vb.x} ${vb.y} ${vb.width} ${vb.height}`);
    this.fit();
  }

  destroy(): void {
    this.cancelAnim();
    this.listeners.clear();
  }

  // ---- internals ----

  /** Size the viewport: as tall as the sheet at the current width, capped at its max-height. */
  private sizeViewport(): void {
    const width = this.viewport.getBoundingClientRect().width;
    if (!width) return;
    const sheetAspect = this.base.w / this.base.h;
    const maxHeight = parseFloat(getComputedStyle(this.viewport).maxHeight);
    let height = width / sheetAspect;
    if (!Number.isNaN(maxHeight) && height > maxHeight) height = maxHeight;
    this.viewport.style.height = `${height}px`;
  }

  /** A region-aspect viewBox enclosing the whole sheet (centred), = scale 1 / fit. */
  private computeFit(): void {
    const rect = this.viewport.getBoundingClientRect();
    // No layout yet (e.g. inside a folded diff box): keep the base-seeded fitBox rather than
    // dividing by zero into a NaN viewBox. The ResizeObserver re-fits once dimensions are real.
    if (!rect.width || !rect.height) return;
    const regionAspect = rect.width / rect.height;
    const sheetAspect = this.base.w / this.base.h;
    let w: number;
    let h: number;
    if (regionAspect > sheetAspect) {
      // viewport is wider than the sheet -> pillarbox (extend viewBox width)
      h = this.base.h;
      w = h * regionAspect;
    } else {
      // viewport is taller -> letterbox (extend viewBox height)
      w = this.base.w;
      h = w / regionAspect;
    }
    const cx = this.base.x + this.base.w / 2;
    const cy = this.base.y + this.base.h / 2;
    this.fitBox = {x: cx - w / 2, y: cy - h / 2, w, h};
  }

  private fit(): void {
    this.sizeViewport();
    this.computeFit();
    this.apply({...this.fitBox});
  }

  /** SVG screen box, cached (viewBox-invariant) to avoid a getBoundingClientRect per pointermove. */
  private svgRect(): DOMRect {
    let rect = this.cachedSvgRect;
    if (!rect?.width) {
      rect = this.svgs[0].getBoundingClientRect();
      this.cachedSvgRect = rect;
    }
    return rect;
  }

  /** Map a client point to a fraction (0..1) of the rendered SVG box. */
  private clientFraction(clientX: number, clientY: number): {fx: number; fy: number} {
    // Fresh read (not the cache): zoom needs scroll-accurate left/top.
    const rect = this.svgs[0].getBoundingClientRect();
    return {
      fx: rect.width ? (clientX - rect.left) / rect.width : 0.5,
      fy: rect.height ? (clientY - rect.top) / rect.height : 0.5,
    };
  }

  /** Clamp size to [fit/maxScale, fit] (keeping region aspect) and position within the fit box. */
  private clampBox(box: ViewBox): ViewBox {
    const minW = this.fitBox.w / this.maxScale;
    const maxW = this.fitBox.w / this.minScale;
    const w = clamp(box.w, minW, maxW);
    const h = w * (this.fitBox.h / this.fitBox.w);
    const x = clamp(box.x, this.fitBox.x, this.fitBox.x + this.fitBox.w - w);
    const y = clamp(box.y, this.fitBox.y, this.fitBox.y + this.fitBox.h - h);
    return {x, y, w, h};
  }

  private apply(box: ViewBox): void {
    this.cur = box;
    const value = `${box.x} ${box.y} ${box.w} ${box.h}`;
    for (const svg of this.svgs) {
      svg.setAttribute('viewBox', value);
    }
    for (const listener of this.listeners) listener();
  }

  private animateTo(target: ViewBox): void {
    this.cancelAnim();
    const start = {...this.cur};
    const startTime = performance.now();
    const stepFn = (now: number): void => {
      const t = Math.min(1, (now - startTime) / this.animationMs);
      const eased = t * (2 - t); // easeOutQuad
      this.apply({
        x: start.x + (target.x - start.x) * eased,
        y: start.y + (target.y - start.y) * eased,
        w: start.w + (target.w - start.w) * eased,
        h: start.h + (target.h - start.h) * eased,
      });
      this.rafId = t < 1 ? requestAnimationFrame(stepFn) : null;
    };
    this.rafId = requestAnimationFrame(stepFn);
  }

  private cancelAnim(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
}
